import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Anordnung } from "../shared/anordnung.ts";
import { formatDeployTime } from "../shared/deployTime.ts";
import type { Umriss } from "../shared/umriss.ts";
import {
  anordnungSpeichern,
  bildEntfernen,
  bildHochladen,
  bilderHolen,
  MeldbarerFehler,
  personenSpeichern,
  umrissSpeichern,
  type BildAusListe,
} from "./lib/api.ts";
import { absetzen, alsReihe, mitGroesse, mitVersatz } from "./lib/anordnung.ts";
import { fehleranzeige, ueberschrift, type Fehlschlag } from "./lib/fehlerliste.ts";
import { projektId, projektVergessen } from "./lib/projekt.ts";
import { beimVerlassen, speicherer } from "./lib/speichern.ts";
import Bildrate, { messenGewuenscht } from "./Bildrate.tsx";
import Reihe from "./Reihe.tsx";
import Umfahren from "./Umfahren.tsx";
import Zuordnung from "./Zuordnung.tsx";
import { HINWEIS_BIS_ZU_DEN_KNIEN } from "./lib/vorschau.ts";

/**
 * Die Schritte 2 bis 5 aus `PROTOTYP-PLAN.md`: Fotos aufnehmen, jede Person umfahren,
 * die Reihe legen, und festhalten, wen eine Figur zeigt.
 *
 * Diese Datei hält den Stand und redet mit dem Server. Die Reihe selbst steht in
 * `src/Reihe.tsx`, das Zeichnen in `src/Umfahren.tsx` — beide bekommen ihre Daten von
 * hier und melden zurück, was sich geändert hat.
 *
 * **Die Warteschlange** ist die Abweichung von ARCHITEKTUR.md 6.2, und sie ist bewusst:
 * dort steht „Bild für Bild — hochladen, umfahren, nächstes Bild". Hier lädt erst der
 * ganze Stapel, danach führt die Warteschlange durch die Fotos. Der Grund ist das
 * Telefon: verschränkt stünde der Upload still, solange jemand zeichnet, und aus einem
 * Vorgang würden zwölf unvorhersehbare Pausen. Was 6.2 ausschließen wollte — „man
 * verliert den Überblick, welche Person schon bearbeitet ist" — erledigt die
 * Warteschlange selbst: sie führt die Liste, nicht die Kundschaft.
 *
 * Jeder Text ist deutsch, auch jeder Fehlertext (`CLAUDE.md`).
 */

/** Wie lange vor dem Ablauf die Liste neu geholt wird (D5, Nachsignieren). */
const NACHSIGNIEREN_VORLAUF_MS = 5 * 60 * 1000;

interface ImGange {
  name: string;
  /** Laufende Nummer in diesem Stapel, für die Anzeige „3 von 12". */
  nummer: number;
  gesamt: number;
}

/** Ein Fehler, der angezeigt wird — mitsamt der Frage, welche Rettung dazu passt. */
interface Anzeigefehler {
  text: string;
  /** Nur dann hilft ein neues Projekt. Sonst wäre es der Verlust der eigenen Arbeit. */
  sitzungWeg: boolean;
}

/** Ein Foto, das nicht durchkam — die Datei bleibt, damit ein zweiter Versuch geht. */
interface GescheitertesFoto extends Fehlschlag {
  datei: File;
  sitzungWeg: boolean;
}

export default function App() {
  const [id, setId] = useState<string | null>(null);
  const [bilder, setBilder] = useState<BildAusListe[]>([]);
  const [imGange, setImGange] = useState<ImGange | null>(null);
  const [fehler, setFehler] = useState<Anzeigefehler | null>(null);
  const [gescheitert, setGescheitert] = useState<GescheitertesFoto[]>([]);
  const [stapelGroesse, setStapelGroesse] = useState(0);
  const [laedt, setLaedt] = useState(true);
  /** Wann die URLs in `bilder` ablaufen — der Auslöser fürs Nachsignieren (D5). */
  const [gueltigBis, setGueltigBis] = useState(0);
  /** Welches Foto gerade im Vollbild umfahren wird, oder `null`. */
  const [zeichnetId, setZeichnetId] = useState<string | null>(null);
  /** Welche Figur gerade auf dem Bildschirm „Bild bearbeiten" liegt, oder `null`
   *  (Schritt 5). Zwei getrennte Zustände und kein gemeinsamer: die beiden Bildschirme
   *  rufen einander auf, und ein Feld mit zwei Bedeutungen wäre genau die Stelle, an der
   *  später beide gleichzeitig offen sind. */
  const [zuordnetId, setZuordnetId] = useState<string | null>(null);
  const dateiFeld = useRef<HTMLInputElement>(null);

  /** Gibt die frische Liste **zurück**, statt sie nur in den Zustand zu legen: die
   *  Warteschlange muss unmittelbar danach wissen, welches Foto als erstes dran ist,
   *  und ein gerade gesetzter Zustand steht im selben Durchlauf noch nicht bereit. */
  const listeHolen = useCallback(async (projekt: string) => {
    const liste = await bilderHolen(projekt);
    setBilder(liste.bilder);
    setGueltigBis(liste.gueltigBis);
    return liste.bilder;
  }, []);

  useEffect(() => {
    let abgebrochen = false;
    void (async () => {
      try {
        const projekt = await projektId();
        if (abgebrochen) return;
        setId(projekt);
        await listeHolen(projekt);
      } catch (problem) {
        if (!abgebrochen) setFehler(alsAnzeigefehler(problem));
      } finally {
        if (!abgebrochen) setLaedt(false);
      }
    })();
    return () => {
      abgebrochen = true;
    };
  }, [listeHolen]);

  /**
   * Nachsignieren (D5): die presignten URLs gelten eine Stunde, und wer 60 Bilder legt,
   * ist länger dabei. Kurz vor Ablauf wird die Liste neu geholt — das ist derselbe
   * Aufruf, der sie ausstellt.
   *
   * Als eigener Effekt am Ablaufzeitpunkt und **nicht** als Zeitgeber, den `listeHolen`
   * sich selbst stellt: ein `setTimeout`, das die eigene Funktion aufruft, wäre eine
   * Selbstreferenz, die beim nächsten Umbau still auf eine alte Fassung zeigt.
   */
  useEffect(() => {
    if (!id || gueltigBis === 0) return;
    const inMs = Math.max(30_000, gueltigBis - Date.now() - NACHSIGNIEREN_VORLAUF_MS);
    const timer = setTimeout(() => {
      void listeHolen(id).catch(() => {
        // Ein misslungenes Nachsignieren ist kein Grund, die Seite zu stören: die alten
        // URLs gelten noch, und der nächste Versuch kommt mit der nächsten Änderung.
      });
    }, inMs);
    return () => {
      clearTimeout(timer);
    };
  }, [id, gueltigBis, listeHolen]);

  /**
   * Einen Stapel hochladen — beim ersten Versuch oder beim zweiten.
   *
   * Ein einzelnes Foto, das scheitert, beendet den Stapel **nicht**. Wer 60 Fotos
   * schickt, will nicht bei Nummer 7 von vorn anfangen; die Datei bleibt im Zustand,
   * damit genau sie erneut geschickt werden kann.
   */
  const hochladeStapel = useCallback(
    async (projekt: string, dateien: readonly File[]) => {
      if (dateien.length === 0) return;
      setFehler(null);
      setGescheitert([]);
      setStapelGroesse(dateien.length);

      const misslungen: GescheitertesFoto[] = [];

      for (const [index, datei] of dateien.entries()) {
        setImGange({ name: datei.name, nummer: index + 1, gesamt: dateien.length });
        try {
          // Der Auflösungshinweis wird hier **nicht** mehr eingesammelt (29.09.2026):
          // ein Dateiname sagt niemandem, welche Kachel gemeint ist, und bei mehreren
          // kleinen Fotos stand nicht einmal der da. Er hängt jetzt am Bild selbst —
          // `zuKlein` kommt mit jeder Liste mit (`src/lib/api.ts`).
          await bildHochladen(projekt, datei);
        } catch (problem) {
          const angezeigt = alsAnzeigefehler(problem);
          misslungen.push({
            datei,
            name: datei.name,
            text: angezeigt.text,
            sitzungWeg: angezeigt.sitzungWeg,
          });
        }
      }

      setImGange(null);
      setGescheitert(misslungen);

      try {
        const frisch = await listeHolen(projekt);
        // Die Warteschlange öffnet sich von selbst, sobald der Stapel durch ist. Wer
        // gerade Fotos geschickt hat, ist im Vorgang — ein Knopf, den man erst finden
        // muss, wäre hier eine Unterbrechung.
        setZeichnetId(frisch.find((bild) => bild.umriss === null)?.bildId ?? null);
      } catch (problem) {
        setFehler(alsAnzeigefehler(problem));
      }
    },
    [listeHolen],
  );

  /**
   * Einen Umriss speichern und zum nächsten offenen Foto weitergehen.
   *
   * Das nächste Foto wird aus der **alten** Liste bestimmt, nur ohne das gerade
   * gespeicherte: mehr hat sich nicht geändert, und ein zweiter Netzaufruf, nur um die
   * Reihenfolge zu erfahren, würde die Warteschlange bei jedem Foto stocken lassen.
   */
  const umrissSetzen = useCallback(
    async (bildId: string, umriss: Umriss, personen: number) => {
      if (!id) return;
      const vorher = bilder.find((bild) => bild.bildId === bildId);
      try {
        await umrissSpeichern(id, bildId, umriss);
        // Nur wenn die Zahl sich geändert hat. Der Normalfall ist eine Person, und dafür
        // soll die Warteschlange nicht bei jedem Foto eine zweite Anfrage schicken —
        // sechzig Fotos wären sonst hundertzwanzig.
        if (personen !== vorher?.personen) {
          await personenSpeichern(id, bildId, personen, vorher?.beschriftung ?? "");
        }
      } catch (problem) {
        // Beim Fehler bleibt der Bildschirm zu und der Fehler steht in der Liste. Der
        // Umriss ist nicht verloren — das Foto bleibt offen und kommt wieder.
        setFehler(alsAnzeigefehler(problem));
        setZeichnetId(null);
        return;
      }
      setBilder((bisher) =>
        bisher.map((bild) => (bild.bildId === bildId ? { ...bild, umriss, personen } : bild)),
      );
      setZeichnetId(
        bilder.find((bild) => bild.umriss === null && bild.bildId !== bildId)?.bildId ?? null,
      );
    },
    [id, bilder],
  );

  /**
   * Wen eine Figur zeigt (Schritt 5): Personenzahl und Name speichern.
   *
   * Gibt zurück, ob es geklappt hat — der aufrufende Bildschirm schließt sich nur dann.
   * Bei einem Fehler bliebe er sonst mit einer Eingabe stehen, die niemand mehr
   * wiederfindet.
   */
  const personenSetzen = useCallback(
    async (bildId: string, anzahl: number, beschriftung: string): Promise<boolean> => {
      if (!id) return false;
      let gespeichert: string;
      try {
        gespeichert = await personenSpeichern(id, bildId, anzahl, beschriftung);
      } catch (problem) {
        setFehler(alsAnzeigefehler(problem));
        return false;
      }
      // Übernommen wird, was der Server zurückgibt — er hat den Text gereinigt. Sonst
      // stünde auf dem Bildschirm etwas anderes als in der Zeile.
      setBilder((bisher) =>
        bisher.map((bild) =>
          bild.bildId === bildId ? { ...bild, personen: anzahl, beschriftung: gespeichert } : bild,
        ),
      );
      return true;
    },
    [id],
  );

  /**
   * Die Reihe speichern, entprellt (Schritt 4 und die Browser-Hälfte von Schritt 6).
   *
   * Der Speicherer hängt an `id`, und das ist nachgerechnet unbedenklich: die ID wird
   * genau **einmal** gesetzt, beim ersten Laden, und danach nie wieder — „Neu anfangen"
   * lädt die Seite neu. Solange sie fehlt, gibt es keine Fotos und damit nichts zu
   * speichern. Ein neu gebauter Speicherer kann hier also keine laufende Uhr verwerfen.
   *
   * Ein Fehler beim Speichern geht in denselben roten Kasten wie alles andere
   * (`src/lib/fehlerliste.ts`) — insbesondere die 429 aus D3, wenn jemand schneller
   * schiebt, als das Kontingent erlaubt.
   */
  const ablage = useMemo(
    () =>
      speicherer<Anordnung[]>((elemente, amEnde) => {
        if (!id) return;
        void anordnungSpeichern(id, elemente, amEnde).catch((problem: unknown) => {
          setFehler(alsAnzeigefehler(problem));
        });
      }),
    [id],
  );

  useEffect(() => {
    const abmelden = beimVerlassen(ablage.jetzt);
    return () => {
      abmelden();
      // Beim Aufräumen **schicken** statt verwerfen: sonst wäre der letzte Handgriff vor
      // einem Neuladen weg, und genau das prüft das Abnahmekriterium von Schritt 6.
      ablage.jetzt();
    };
  }, [ablage]);

  /** Ein geänderter Stand: erst anzeigen, dann entprellt speichern. */
  const anordnungAendern = useCallback(
    (naechste: BildAusListe[]) => {
      setBilder(naechste);
      ablage.aendern(alsReihe(naechste));
    },
    [ablage],
  );

  const figurAbsetzen = useCallback(
    (von: number, platz: number) => {
      anordnungAendern(absetzen(bilder, von, platz));
    },
    [anordnungAendern, bilder],
  );

  const figurGroesse = useCallback(
    (bildId: string, wert: number) => {
      anordnungAendern(mitGroesse(bilder, bildId, wert));
    },
    [anordnungAendern, bilder],
  );

  const figurVersatz = useCallback(
    (bildId: string, wert: number) => {
      anordnungAendern(mitVersatz(bilder, bildId, wert));
    },
    [anordnungAendern, bilder],
  );

  const figurZuordnen = useCallback((bildId: string) => {
    setZuordnetId(bildId);
  }, []);

  /**
   * Ein Bild ganz aus dem Projekt nehmen (Annas Test am 29.09.2026).
   *
   * ⚠️ **Die Reihenfolge ist tragend.** Erst den ausstehenden Stand verwerfen, dann
   * löschen, dann die frische Reihe schicken. Ein entprellter Schreibvorgang mit der
   * **alten** Reihe käme sonst nach dem Löschen an, und der Server weist eine Reihe, in
   * der eine Figur steht, die es nicht mehr gibt, mit **401** ab (`server/app.ts`,
   * Anordnungsroute). Die Oberfläche liest aus 401 „Sitzung weg" und bietet dann nur noch
   * „Neu anfangen" an (`src/lib/fehlerliste.ts`) — aus einem gelöschten Bild würde so der
   * Verlust der ganzen Collage.
   *
   * Verworfen wird dabei **keine** Arbeit: `bilder` trägt den ausstehenden Stand bereits,
   * weil `anordnungAendern` zuerst anzeigt und dann speichert. `rest` ist also der aktuelle
   * Stand ohne dieses eine Bild, und `alsReihe` numeriert ihn nach Platz — die Lücke aus
   * dem Löschen ist damit weg (`src/lib/anordnung.ts`).
   *
   * ⚠️ **Nicht geschlossen ist der Fall, in dem ein Schreibvorgang schon unterwegs ist**,
   * wenn hier gelöscht wird. Er steht in `TODO.md`; praktisch liegen zwischen der letzten
   * Änderung an der Reihe und diesem Knopf drei Fingertipps und ein Bildschirmwechsel,
   * also deutlich mehr als die zwei Sekunden Entprellung.
   */
  const bildWegnehmen = useCallback(
    async (bildId: string) => {
      if (!id) return;
      const rest = bilder.filter((bild) => bild.bildId !== bildId);
      ablage.abbrechen();
      try {
        await bildEntfernen(id, bildId);
      } catch (problem) {
        setFehler(alsAnzeigefehler(problem));
        return;
      }
      setBilder(rest);
      setZuordnetId(null);
      if (rest.length > 0) ablage.aendern(alsReihe(rest));
    },
    [id, bilder, ablage],
  );

  function dateienGewaehlt(event: React.ChangeEvent<HTMLInputElement>) {
    const dateien = Array.from(event.target.files ?? []);
    // Das Feld sofort leeren: sonst löst dieselbe Datei ein zweites Mal kein Ereignis
    // aus, und ein zweiter Versuch nach einem Fehler wäre unmöglich.
    event.target.value = "";
    if (!id) return;
    void hochladeStapel(id, dateien);
  }

  // Ist die Sitzung weg, hilft nur ein neues Projekt. Bei allem anderen wäre genau das
  // der Schaden — siehe `neuAnfangen` unten.
  const sitzungWeg = fehler?.sitzungWeg === true || gescheitert.some((f) => f.sitzungWeg);
  // Ein Ort entscheidet, wie viele rote Kästen dastehen (`src/lib/fehlerliste.ts`).
  // Vorher rechnete das die Komponente nebenbei — und zeigte bei verlorener Sitzung
  // dreimal dieselbe Aussage.
  const anzeige = fehleranzeige(fehler?.text ?? null, gescheitert, sitzungWeg);
  const offen = bilder.filter((bild) => bild.umriss === null);
  const zeichnetIndex = bilder.findIndex((bild) => bild.bildId === zeichnetId);
  const zeichnetBild = zeichnetIndex === -1 ? null : bilder[zeichnetIndex];
  const zuordnetIndex = bilder.findIndex((bild) => bild.bildId === zuordnetId);
  const zuordnetBild = zuordnetIndex === -1 ? null : bilder[zuordnetIndex];

  if (zeichnetBild) {
    return (
      <Umfahren
        // Der Schlüssel zwingt einen frischen Zustand je Foto. Ohne ihn behielte der
        // nächste Bildschirm den halb gezogenen Pfad des vorherigen.
        key={zeichnetBild.bildId}
        bild={zeichnetBild}
        nummer={zeichnetIndex + 1}
        gesamt={bilder.length}
        onSpeichern={(umriss, personen) => void umrissSetzen(zeichnetBild.bildId, umriss, personen)}
        // Raus aus der Warteschlange, nicht nur aus diesem Foto: wer „Zurück" tippt,
        // will die Reihe sehen und nicht das nächste Bild. Was offen blieb, holt der
        // Knopf „Noch N Fotos umfahren" weiter unten zurück.
        onZurueck={() => {
          setZeichnetId(null);
        }}
      />
    );
  }

  if (zuordnetBild) {
    return (
      <Zuordnung
        // Derselbe Grund wie oben: ein Wechsel der Figur muss die Eingaben frisch
        // aufsetzen, sonst stünde der Name der vorigen im Feld.
        key={zuordnetBild.bildId}
        bild={zuordnetBild}
        nummer={zuordnetIndex + 1}
        onSpeichern={(anzahl, beschriftung) => {
          void personenSetzen(zuordnetBild.bildId, anzahl, beschriftung).then((geklappt) => {
            if (geklappt) setZuordnetId(null);
          });
        }}
        onUmfahren={(anzahl, beschriftung) => {
          void personenSetzen(zuordnetBild.bildId, anzahl, beschriftung).then((geklappt) => {
            if (!geklappt) return;
            setZuordnetId(null);
            setZeichnetId(zuordnetBild.bildId);
          });
        }}
        onEntfernen={() => void bildWegnehmen(zuordnetBild.bildId)}
      />
    );
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col gap-4 px-4 py-6">
      {/* ⚠️ **Der Kopf wird schmal, sobald Fotos da sind** (24.09.2026, nach der Messung
          auf einem 667 px hohen Bildschirm). Vorher standen Titel, drei Zeilen Anleitung,
          ein 56-px-Knopf und ein zweizeiliger Hinweis dauerhaft über der Reihe — rund
          390 px, und damit lag die Bodenlinie auf einem iPhone SE unter dem Rand. Die
          Anleitung ist eine Einstiegshilfe; wer schon Fotos hochgeladen hat, braucht sie
          nicht mehr, sondern den Platz. ARCHITEKTUR.md 6.2 sieht oben ohnehin nur eine
          schmale Zeile vor. */}
      <header className="flex flex-col gap-2">
        {/* Die Serife zitiert Annas Seite (dort steht jede Überschrift in Ovo). Sie steht
            bewusst NUR hier: die übrigen Überschriften sind Zähler und Bildnummern, also
            Anzeigen, die man im Betrieb liest — die bleiben in der Systemschrift.
            Begründung und die Entscheidung gegen geladene Schriften in `src/index.css`. */}
        <h1
          className={
            bilder.length > 0
              ? "font-titel text-lg font-semibold text-neutral-900"
              : "font-titel text-2xl font-semibold text-neutral-900"
          }
        >
          Collagen-Planer
        </h1>
        {bilder.length === 0 && (
          /* Der Satz sagt seit Schritt 4 nicht mehr „Anordnen kommt im nächsten Schritt" —
             das Anordnen ist jetzt hier, und ein Versprechen auf später wäre falsch. */
          <p className="text-neutral-700">
            Lade die Fotos hoch, aus denen deine Collage entstehen soll. Danach fährst du um jede
            Person herum und stellst sie in die Reihe.
          </p>
        )}
      </header>

      <section className="flex flex-col gap-3">
        <button
          type="button"
          onClick={() => dateiFeld.current?.click()}
          disabled={!id || imGange !== null}
          // 56 px hoch: auf einem Telefon wird dieser Knopf mit dem Daumen getroffen,
          // oft im Stehen. Die 44 px aus den Apple-Richtlinien sind das Minimum, nicht
          // das Ziel. Sind schon Fotos da, reicht die kleinere Form — dann ist er nicht
          // mehr der Anfang, sondern ein Nachschlag.
          className={
            bilder.length > 0
              ? "min-h-11 self-start rounded-xl border border-neutral-900 px-4 text-sm font-medium text-neutral-900 disabled:border-neutral-400 disabled:text-neutral-400"
              : "min-h-14 rounded-xl bg-neutral-900 px-5 text-base font-medium text-white disabled:bg-neutral-400"
          }
        >
          {imGange ? "Bitte warten …" : bilder.length > 0 ? "Weitere Fotos" : "Fotos auswählen"}
        </button>
        <input
          ref={dateiFeld}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
          multiple
          onChange={dateienGewaehlt}
          className="hidden"
        />
        {/* Der Hinweis steht am Knopf und nicht auf einer Hilfeseite: der Zuschnitt
            ist zugleich die Auswahl der abgebildeten Personen (ARCHITEKTUR.md 6.4).
            Er gilt beim Auswählen — danach nimmt er nur Platz weg. */}
        {bilder.length === 0 && (
          <p className="text-sm text-neutral-600">{HINWEIS_BIS_ZU_DEN_KNIEN}</p>
        )}
      </section>

      {imGange && (
        <p
          aria-live="polite"
          className="rounded-lg bg-neutral-100 px-4 py-3 text-sm text-neutral-800"
        >
          Lädt {imGange.nummer} von {imGange.gesamt}: {imGange.name}
        </p>
      )}

      {anzeige.einzeln && (
        <p role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-900">
          {anzeige.einzeln}
        </p>
      )}

      {anzeige.gruppen.length > 0 && (
        <section aria-live="polite" className="flex flex-col gap-3">
          {/* Gebündelt nach dem Satz, nicht je Datei: derselbe Text sechzig Mal
              untereinander ist auf 390 px eine Wand (src/lib/fehlerliste.ts). */}
          {anzeige.gruppen.map((gruppe) => (
            <div
              key={gruppe.text}
              className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-900"
              role="alert"
            >
              <p>{ueberschrift(gruppe, stapelGroesse)}</p>
              {gruppe.namen.length > 1 && (
                <details className="mt-2">
                  <summary className="cursor-pointer underline">Welche Fotos?</summary>
                  <ul className="mt-1 list-disc ps-5">
                    {gruppe.namen.map((name) => (
                      <li key={name} className="break-all">
                        {name}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          ))}

          {/* Der Weg zurück, den es vorher nicht gab: die Dateien liegen noch im
              Zustand, also muss niemand sie im Foto-Auswähler wiederfinden. */}
          {!sitzungWeg && id && (
            <button
              type="button"
              onClick={() =>
                void hochladeStapel(
                  id,
                  gescheitert.map((f) => f.datei),
                )
              }
              disabled={imGange !== null}
              className="min-h-14 rounded-xl border border-neutral-900 px-5 text-base font-medium text-neutral-900 disabled:border-neutral-400 disabled:text-neutral-400"
            >
              {gescheitert.length === 1
                ? "Dieses Foto erneut versuchen"
                : `${String(gescheitert.length)} Fotos erneut versuchen`}
            </button>
          )}
        </section>
      )}

      {/* „Neu anfangen" gibt es NUR bei verlorener Sitzung. Vorher stand der Knopf an
          jedem Fehler — auch an einem abgerissenen Netz nach erfolgreichem Stapel, und
          dort wirft er die gemerkte Projekt-ID weg: die Fotos bleiben im Speicher
          liegen und sind für die Kundin nicht mehr erreichbar. */}
      {anzeige.neuAnfangen && (
        <div role="alert" className="flex flex-col gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm">
          <p className="text-red-900">
            Diese Collage lässt sich auf diesem Gerät nicht mehr öffnen. Ein neuer Anfang legt eine
            leere Sammlung an — die bisherigen Fotos sind dann nicht mehr erreichbar.
          </p>
          <button
            type="button"
            onClick={() => {
              projektVergessen();
              location.reload();
            }}
            className="min-h-14 self-start rounded-xl bg-neutral-900 px-5 text-base font-medium text-white"
          >
            Neu anfangen
          </button>
        </div>
      )}

      <section className="flex flex-col gap-3">
        {/* Die Zählung steht nur, solange es keine Reihe gibt — sonst trägt sie die
            Kopfzeile der Reihe (`src/Reihe.tsx`). Zwei Überschriften übereinander sind
            auf einem 667 px hohen Bildschirm rund 70 px, die der Bodenlinie fehlen. */}
        {(laedt || bilder.length === 0) && (
          <h2 className="text-lg font-medium text-neutral-900">
            {laedt ? "Fotos werden geladen …" : anzahlFotos(bilder.length)}
          </h2>
        )}

        {/* Nach dem Hochladen öffnet sich die Warteschlange von selbst. Dieser Knopf ist
            der Weg zurück hinein, wenn jemand die Seite zwischendurch geschlossen hat —
            ohne ihn müsste man raten, dass eine Kachel antippbar ist. Er erscheint nur,
            wenn es wirklich etwas zu tun gibt. */}
        {offen.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setZeichnetId(offen[0]?.bildId ?? null);
            }}
            className="min-h-14 rounded-xl bg-neutral-900 px-5 text-base font-medium text-white"
          >
            {offen.length === 1
              ? "Noch 1 Foto umfahren"
              : `Noch ${String(offen.length)} Fotos umfahren`}
          </button>
        )}
      </section>

      {/* Die Reihe mit Bodenlinie (Schritt 4). Sie steht in einer eigenen Komponente,
          weil sie ihren eigenen Zustand hat — Auswahl, aufgenommene Figur, Vorschau —
          und weil eine Auswahl nicht den Upload-Teil dieser Seite neu rendern soll
          (ARCHITEKTUR.md 3.1). */}
      {bilder.length > 0 && (
        <Reihe
          bilder={bilder}
          onAbsetzen={figurAbsetzen}
          onGroesse={figurGroesse}
          onVersatz={figurVersatz}
          onZuordnung={figurZuordnen}
          onEntfernen={(bildId) => void bildWegnehmen(bildId)}
        />
      )}

      {/* ⚠️ **Hier stand bis zum 29.09.2026 der Auflösungshinweis** — und zwar dauerhaft,
          solange irgendein Bild zu klein war, ohne zu sagen, welches. Dazu kam nach jedem
          Hochladen ein zweiter Kasten weiter oben, der bei mehreren kleinen Fotos nur
          „3 Fotos: …" sagte. Anna wusste in ihrem Test deshalb nicht, welches Bild gemeint
          war (`TODO.md`).

          Beides ist weg. Der Hinweis hängt jetzt am Bild: ein Zeichen an der Kachel in der
          Reihe (`src/Reihe.tsx`) und der ganze Satz auf „Bild bearbeiten", wo das Foto groß
          liegt (`src/Zuordnung.tsx`). Damit beantwortet er die Frage „welches?" durch
          seinen Ort statt durch einen Dateinamen, den auf einem Telefon ohnehin niemand
          wiedererkennt. */}

      {/* Nur mit `?messen=1` in der Adresse. Der Zähler ist das Messgerät für P5 und
          gehört nicht vor Annas Kundschaft (`src/Bildrate.tsx`). */}
      {messenGewuenscht(window.location.search) && <Bildrate />}

      <footer className="mt-auto text-xs text-neutral-500">
        Stand dieser Fassung: {formatDeployTime(__BUILD_TIME__)}
      </footer>
    </main>
  );
}

/**
 * „1 Fotos" stand hier bis zum 24.09.2026 — ein Fehler, den beide Reviews nicht gesehen
 * haben, weil er genau bei einem einzigen Foto auftritt und sonst nie. Deutsche Texte
 * stehen vor einer Kundin, auch die aus einer Zählung zusammengesetzten.
 */
function anzahlFotos(anzahl: number): string {
  if (anzahl === 0) return "Noch keine Fotos";
  if (anzahl === 1) return "1 Foto";
  return `${String(anzahl)} Fotos`;
}

/** Nur selbst formulierte Sätze gehen nach vorn — alles andere wird allgemein. */
function alsAnzeigefehler(problem: unknown): Anzeigefehler {
  if (problem instanceof MeldbarerFehler) {
    return { text: problem.message, sitzungWeg: problem.sitzungWeg };
  }
  return {
    text: "Da ist etwas schiefgegangen. Bitte versuch es gleich noch einmal.",
    sitzungWeg: false,
  };
}
